import { useAnalyticContext } from '@application/analytic/analytic_link/mod';
import { withTranslation } from '@application/i18n/with_translation';
import { BreakPoints, Button, DropdownMenu, Flex, IconButton, Text, useBreakpoint } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { RouterService } from '@cortex-shim/router.service';
import { useResolve } from '@visiba/instantiation';
import { useWindowSize } from '@visiba/react-std';
import styled from 'styled-components';

const elements = {
	container: styled.div`
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: ${tokens.spacing.x2};
		height: 56px;
		background-color: ${tokens.color.bg.surface};
		border-bottom: 1px solid ${tokens.color.border.muted};
		word-wrap: break-word;
		word-break: break-all;
		gap: ${tokens.spacing.x2};

		.side-section {
			flex-shrink: 0;
			min-width: 140px;

			* {
				word-break: keep-all;
				white-space: nowrap;
				flex-shrink: 0;
			}
		}

		&[data-extra-small-screen] {
			padding-left: ${tokens.spacing.x1};

			.side-section {
				min-width: 24px;
			}
		}
	`,
};

export type NavBarAction = { label: string; onSelect: () => void };

interface Props {
	to?: string;
	title?: string;
	analyticAction: string;
	actions?: NavBarAction[];
}

export function ViewNavBar({ to, analyticAction, title, actions }: Props): JSX.Element {
	const routerService = useResolve(RouterService);
	const analytic = useAnalyticContext();
	const { width: windowWidth } = useWindowSize();
	const isExtraSmallScreen = windowWidth && windowWidth < 440;
	const isMobile = useBreakpoint(BreakPoints.Small);

	function handleBackClick() {
		if (!to) {
			return;
		}

		analytic.trackEvent(analyticAction);
		routerService.navigateByUrl(to);
	}

	const renderBackButton = () => {
		if (!isMobile) {
			return null;
		}
		if (isExtraSmallScreen) {
			return (
				<IconButton
					variant='interactive'
					size='small'
					icon='arrowLeft'
					onClick={handleBackClick}
					text={withTranslation('PW.Component.Visits.Title')}
				/>
			);
		}
		return (
			<Button variant='ghost' size='xSmall' icon='arrowLeft' onClick={handleBackClick}>
				{withTranslation('PW.Component.Visits.Title')}
			</Button>
		);
	};

	return (
		<elements.container data-extra-small-screen={isExtraSmallScreen || undefined}>
			<Flex align='start' className='side-section'>
				{to != null ? <>{renderBackButton()}</> : null}
			</Flex>
			<Flex grow={1}>
				<Text
					variant='labelSmallRegular'
					style={{
						color: tokens.color.content.muted,
						textAlign: 'center',
					}}
				>
					{title}
				</Text>
			</Flex>
			<Flex align='end' className='side-section'>
				{actions && actions.length > 0 ? (
					<DropdownMenu.Root
						trigger={
							<IconButton
								icon='ellipsisVertical'
								size='small'
								text={withTranslation('PW.Component.Visits.MessagingInfo.MenuTooltip')}
								variant='interactive'
							/>
						}
					>
						{actions.map((action) => (
							<DropdownMenu.Item key={action.label} onSelect={action.onSelect}>
								{action.label}
							</DropdownMenu.Item>
						))}
					</DropdownMenu.Root>
				) : (
					<div />
				)}
			</Flex>
		</elements.container>
	);
}
